import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Backdrop,
  CircularProgress,
  Modal,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Fade,
  Stack,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import backend from "../../../../../api";
import Classes from "./Mentors.module.css";
import FilterDropDown from "./Comp/FilterDropDown";

import { CalenderSimpleIcon, ProfileIcon } from "../../../../../utils/Icons";

/* —— brand color for all MUI states —— */
const PRIMARY = "#f26a63";

/* —— MUI theme override (kept local to this file) —— */
const muiTheme = createTheme({
  palette: {
    primary: { main: PRIMARY },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: PRIMARY,
          color: "#fff",
          "&:hover": { backgroundColor: "#e45a53" },
        },
        outlinedPrimary: {
          borderColor: PRIMARY,
          color: PRIMARY,
          "&:hover": { borderColor: PRIMARY },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY },
        },
      },
    },
    MuiSelect: { styleOverrides: { icon: { color: PRIMARY } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected": { backgroundColor: "rgba(242,106,99,0.12)" },
          "&.Mui-selected.Mui-focusVisible, &.Mui-selected:hover": {
            backgroundColor: "rgba(242,106,99,0.18)",
          },
        },
      },
    },
  },
});

/* —— animation variants —— */
const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: 0.1 + Math.pow(i, 0.8) * 0.1,
      duration: 0.3,
      ease: "easeOut",
    },
  }),
};

// Replace your old genMeetLink
const genMeetLink = ({ roomPrefix = "mentr", subject, displayName } = {}) => {
  const rnd = () => Math.random().toString(36).slice(2, 10);
  const room = `${roomPrefix}-${rnd()}`;
  const base = `https://meet.jit.si/${encodeURIComponent(room)}`;

  const parts = [
    "config.disableDeepLinking=true",
    "config.prejoinConfig.enabled=true",
  ];
  if (subject) parts.push(`config.subject=${encodeURIComponent(subject)}`);
  if (displayName)
    parts.push(`userInfo.displayName=${encodeURIComponent(JSON.stringify(displayName))}`);

  return `${base}#${parts.join("&")}`;
};

/* Helper to format time from HH:MM to h:MM AM/PM */
const formatTime = (time) => {
  const [hours, mins] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const adjustedHours = hours % 12 || 12;
  return `${adjustedHours}:${String(mins).padStart(2, "0")} ${period}`;
};

/* Helper to format date from YYYY-MM-DD to MM-DD-YYYY */
const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

function addMinutes(time, minutes) {
  const [hours, mins] = time.split(":").map(Number);
  let total = hours * 60 + mins + minutes;
  const newHours = Math.floor(total / 60) % 24;
  const newMins = total % 60;
  return `${String(newHours).padStart(2, "0")}:${String(newMins).padStart(2, "0")}`;
}

/* ------------ Booking Modal (duration → date → slot) ------------- */
function BookingModal({ open, onClose, mentor, user, onBook }) {
  const [duration, setDuration] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");

  const availability = mentor?.mentorProfile?.availability || {};
  const timeSettings = availability.timeSettings || {};
  const availableSlots = availability.availableSlots || {};
  const durations = timeSettings.durations || [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dates = Object.keys(availableSlots)
    .filter((d) => !isNaN(Date.parse(d)) && new Date(d) >= today)
    .sort((a, b) => new Date(a) - new Date(b));

    // ✅ helper: checks if a slot is in the past (only matters for "today")
const isPastSlotForSelectedDate = (selectedDate, slotHHMM) => {
  if (!selectedDate || !slotHHMM) return false;

  const now = new Date();

  // Build a Date using selectedDate + slot time in the user's local timezone
  const [y, m, d] = selectedDate.split("-").map(Number);
  const [hh, mm] = slotHHMM.split(":").map(Number);

  const slotDateTime = new Date(y, m - 1, d, hh, mm, 0, 0);

  // If selected date is today, hide slots that already passed
  const isToday =
    slotDateTime.getFullYear() === now.getFullYear() &&
    slotDateTime.getMonth() === now.getMonth() &&
    slotDateTime.getDate() === now.getDate();

  if (!isToday) return false;

  return slotDateTime <= now; // passed (or equal) → hide
};


const visibleSlots =
  date && duration
    ? Object.keys(availableSlots?.[date]?.[duration] || {})
        .filter((k) => availableSlots[date][duration][k] === "available")
        // ✅ hide passed slots for today
        .filter((k) => !isPastSlotForSelectedDate(date, k))
        .sort((a, b) => a.localeCompare(b))
    : [];


  const handleDurationChange = (e) => {
    setDuration(e.target.value);
    setDate("");
    setSlot("");
  };

  const handleDateChange = (e) => {
    setDate(e.target.value);
    setSlot("");
  };

  const handleBook = () => {
    if (!duration || !date || !slot) return;

    const selectedDuration = durations.find((d) => Number(d.time) === Number(duration));
    const end = addMinutes(slot, Number(selectedDuration.time));

    const booking = {
      userId: user?._id,
      mentorId: mentor?._id,
      date,
      start: slot,
      end,
      duration: Number(selectedDuration.time),
      // IMPORTANT: this price is mentor fee only (backend adds $5 and fees)
      price: Number(selectedDuration.price),
      meetLink: genMeetLink(),
      source: "mentors_list",
    };

    onBook(booking);
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open} timeout={180}>
        <Box sx={sxModal}>
          <Box sx={sxModalHeader}>
            <div>
              <Typography variant="h8" sx={{ fontWeight: 700, lineHeight: 1 }}>
                Book a Meeting
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                Pick a duration, date and an available time slot.
              </Typography>
            </div>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Divider />

          <div style={{ padding: "16px", maxHeight: "80vh", overflowY: "auto" }}>
            <Typography sx={labelText}>Select Duration</Typography>
            <Select value={duration} onChange={handleDurationChange} fullWidth size="small">
              <MenuItem value="">Select Duration</MenuItem>
              {durations.map((d, i) => (
                <MenuItem key={i} value={d.time}>
                  {d.time} min — ${d.price}
                </MenuItem>
              ))}
            </Select>

            {duration && (
              <>
                <Typography sx={{ ...labelText, mt: 2 }}>Select Date</Typography>
                <Select value={date} onChange={handleDateChange} fullWidth size="small">
                  <MenuItem value="">Select Date</MenuItem>
                  {dates.map((d) => (
                    <MenuItem key={d} value={d}>
                      {formatDate(d)}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            {date && (
              <>
                <Typography sx={{ ...labelText, mt: 2 }}>Select Slot</Typography>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {visibleSlots.length ? (
                    visibleSlots.map((s) => (
                      <Button
                        key={s}
                        onClick={() => setSlot(s)}
                        variant={slot === s ? "contained" : "outlined"}
                        size="small"
                        sx={{ textTransform: "none" }}
                      >
                        {formatTime(s)}
                      </Button>
                    ))
                  ) : (
                    <Typography sx={{ gridColumn: "1 / -1", fontSize: 13, color: "#777" }}>
                      No available slots for this date.
                    </Typography>
                  )}
                </div>
              </>
            )}
          </div>

          <Divider />

          <div style={{ padding: "16px" }}>
            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              <Button onClick={onClose} variant="outlined" fullWidth>
                Close
              </Button>
              <Button onClick={handleBook} disabled={!slot} variant="contained" fullWidth>
                {duration && date && slot ? "Review Booking" : "Next"}
              </Button>
            </Stack>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}

/* ------------ Payment Preview / Continue to Payment ------------- */
function BookingConfirmationModal({ open, onClose, booking, onPay, paying }) {
  if (!booking) return null;

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open} timeout={180}>
        <Box sx={sxModal}>
          <Box sx={sxModalHeader}>
            <div>
              <Typography variant="h8" sx={{ fontWeight: 700, lineHeight: 1 }}>
                Booking Preview
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                Confirm the details before continuing to payment.
              </Typography>
            </div>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Divider />

          <div style={{ padding: "16px" }}>
            <SummaryRow label="Date" value={formatDate(booking?.date)} />
            <SummaryRow
              label="Time"
              value={`${formatTime(booking?.start)} — ${formatTime(booking?.end)}`}
            />
            <SummaryRow label="Duration" value={`${booking?.duration} min`} />
            <SummaryRow label="Mentor Fee" value={`$${booking?.price}`} />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
              Note: A $5 call fee is added at checkout.
            </Typography>
          </div>

          <Divider />

          <Box sx={sxModalActions}>
            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
              <Button onClick={onClose} variant="outlined" fullWidth>
                Close
              </Button>
              <Button variant="contained" fullWidth onClick={() => onPay(booking)} disabled={!!paying}>
                {paying ? "Redirecting…" : "Continue to Payment"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

function SummaryRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>{value}</Typography>
    </Box>
  );
}

export default function Mentors() {
  const navigate = useNavigate();
  const { user, token } = useSelector((s) => s.auth);

  const [loading, setLoading] = useState(false);
  const [mentors, setMentors] = useState([]);
  const [activeCat, setActive] = useState("All");
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [clearSignal, setClear] = useState(0);

  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const categories = useMemo(() => {
    const tagSet = new Set();
    mentors.forEach((m) =>
      m.mentorProfile?.experience?.howHelp?.forEach((t) => tagSet.add(t))
    );
    return ["All", ...Array.from(tagSet).slice(0, 6)];
  }, [mentors]);

  const fetchMentors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ role: "mentor" });
      Object.entries(filters).forEach(([k, arr]) => {
        if (arr.length) params.append(k, arr.join(","));
      });

      const { data } = await backend.get(`/api/user?${params.toString()}`);
      setMentors(data.data || []);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchMentors();
  }, [fetchMentors]);

  const shownMentors = useMemo(() => {
    const q = query.toLowerCase();

    const matchesAllFilters = (m) => {
      const prof = m.mentorProfile || {};
      const basic = prof.basicInfo || {};
      const exp = prof.experience || {};
      const session = prof.sessionDetails || {};

      for (const [title, need] of Object.entries(filters)) {
        if (!need.length) continue;

        const has = (val) =>
          Array.isArray(val) ? val.some((v) => need.includes(v)) : need.includes(val);

        switch (title) {
          case "Job Title":
            if (!has(basic.title)) return false;
            break;
          case "Education / University":
            if (!has(basic.education)) return false;
            break;
          case "Clubs / Fraternities / Charities":
            if (!has(basic.clubs)) return false;
            break;
          case "Areas of Expertise":
            if (!has(exp.expertise)) return false;
            break;
          case "How Do You Want to Help Mentees":
            if (!has(exp?.howHelp)) return false;
            break;
          case "Level of Experience":
            if (!has(exp.levels)) return false;
            break;
          case "Language":
            if (!has(session.languagesSpoken)) return false;
            break;
          default:
            break;
        }
      }
      return true;
    };

    return mentors.filter((m) => {
      const prof = m.mentorProfile || {};
      const matchesCat = activeCat === "All" || prof.experience?.howHelp?.includes(activeCat);

      const matchesSearch =
        m.fullname?.toLowerCase().includes(q) ||
        prof.basicInfo?.title?.toLowerCase().includes(q) ||
        prof.basicInfo?.company?.toLowerCase?.().includes(q) ||
        prof.basicInfo?.name?.toLowerCase().includes(q) ||
        prof.experience?.expertise?.some((t) => t.toLowerCase().includes(q)) ||
        prof.experience?.howHelp?.some((t) => t.toLowerCase().includes(q)) ||
        prof.sessionDetails?.languagesSpoken?.some((t) => t.toLowerCase().includes(q));

      return matchesCat && matchesSearch && matchesAllFilters(m);
    });
  }, [mentors, query, activeCat, filters]);

  const clearFilters = () => {
    setFilters({});
    setClear((s) => s + 1);
  };
  const filtersActive = Object.values(filters).some((arr) => arr.length);

  // ✅ helper: stripe connected check
  const isMentorStripeConnected = (mentor) => !!mentor?.mentorProfile?.isStripeConnected;

  // ✅ booking handlers — hard block if not connected
  const openBooking = (mentor) => {
    if (!isMentorStripeConnected(mentor)) {
      alert("Booking disabled: Mentor hasn’t connected Stripe yet.");
      return;
    }
    setConfirmationOpen(false);
    setSuccessBooking(null);
    setSelectedMentor(mentor);
    setBookingOpen(true);
  };

  const closeBooking = () => {
    setBookingOpen(false);
    setSelectedMentor(null);
  };

  const handleBook = (booking) => {
    setBookingOpen(false);
    setSelectedMentor(null);
    setSuccessBooking(booking);
    setConfirmationOpen(true);
  };

  const closeConfirmation = () => setConfirmationOpen(false);

  /* —— Stripe: create checkout session —— */
  const handlePayment = async (booking) => {
    try {
      // ✅ final guard (even if UI bypassed)
      const mentorForPayment = mentors.find((m) => String(m._id) === String(booking?.mentorId));
      if (mentorForPayment && !isMentorStripeConnected(mentorForPayment)) {
        alert("This mentor has not connected Stripe yet, so bookings are disabled.");
        return;
      }

      setPaymentLoading(true);

      const payload = {
        metadata: {
          userId: String(booking?.userId),
          mentorId: String(booking?.mentorId),
          date: String(booking?.date),
          start: String(booking?.start),
          end: String(booking?.end),
          duration: String(booking?.duration),
          meetLink: String(booking?.meetLink || ""),
          source: "mentors_list",
          price: String(booking?.price), // mentor fee only, backend adds $5 and % fees
          currency: "usd",
        },
      };

      const resp = await backend.post("/pay/create-checkout-session", payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (resp?.data?.url) {
        window.location.assign(resp.data.url);
      } else {
        alert("Could not start payment.");
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err.message;
      alert("Payment error: " + msg);
    } finally {
      setPaymentLoading(false);
    }
  };

  const getDisplayRating = (m) => {
    const r = Number(m?.mentorProfile?.bookingSetup?.rating) ?? Number(m?.averageRating);
    if (!Number.isFinite(r)) return null;
    const clamped = Math.max(1, Math.min(5, r));
    return clamped > 0 ? clamped : null;
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <>
        <Backdrop open={loading} sx={{ zIndex: 1301, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>

        {bookingOpen && selectedMentor && (
          <BookingModal
            open={bookingOpen}
            onClose={closeBooking}
            mentor={selectedMentor}
            user={user}
            onBook={handleBook}
          />
        )}

        {confirmationOpen && successBooking && (
          <BookingConfirmationModal
            open={confirmationOpen}
            onClose={closeConfirmation}
            booking={successBooking}
            onPay={handlePayment}
            paying={paymentLoading}
          />
        )}

        <div className={Classes.hero}>
          <div className={Classes.header}>
            <div>
              <h3 className={Classes.title3}>Mentors</h3>
              <h5 className={Classes.text}>Explore and schedule time with mentors.</h5>
            </div>

            <div className={Classes.searcCon}>
              <img src="/assets/images/search.png" alt="icon" />
              <input
                type="text"
                placeholder="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          <div className={Classes.tabRow}>
            <div className={Classes.tableRowRight}>
              {categories.map((c) => (
                <button
                  key={c}
                  className={`${Classes.tab} ${activeCat === c ? Classes.activeTab : ""}`}
                  onClick={() => setActive(c)}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="flexAlign10">
              {filtersActive && (
                <button onClick={clearFilters} className={Classes.clearBtn}>
                  Clear all
                </button>
              )}
              <FilterDropDown onFilterChange={setFilters} clearSignal={clearSignal} />
            </div>
          </div>

          {shownMentors?.length ? (
            <div className={Classes.grid}>
              {shownMentors.map((m, i) => {
                const p = m.mentorProfile || {};
                const ratingVal = getDisplayRating(m);
                const stripeConnected = isMentorStripeConnected(m);

                return (
                  <motion.div
                    key={m._id}
                    className={Classes.card}
                    custom={i}
                    initial="hidden"
                    animate="visible"
                    variants={itemVariants}
                  >
                    {ratingVal !== null && (
                      <span className={Classes.rating}>★ {ratingVal.toFixed(1)}</span>
                    )}

                    <div className={Classes.imgWrap}>
                      <img
                        src={p.basicInfo?.avatar || "/assets/images/user.png"}
                        alt={m.fullname}
                        draggable={false}
                      />
                    </div>

                    <h4 className={Classes.name}>{p?.basicInfo?.name}</h4>
                    <h5 className={Classes.role}>
                      {p.basicInfo?.title?.replace(/@.*/, "") || "Mentor"}
                      {p.basicInfo?.company && ","}
                      <span> {p.basicInfo?.company} </span>
                    </h5>

                    <h5 className={Classes.matchLabel}>A GREAT MATCH FOR</h5>
                    <ul className={Classes.tagList}>
                      {(p.experience?.howHelp || []).slice(0, 3).map((t) => (
                        <li key={t}>{t}</li>
                      ))}
                    </ul>

                    {!stripeConnected && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#b00020" }}>
                        Booking disabled: Stripe not connected
                      </div>
                    )}

                    <div className={Classes.actions}>
                      <button
                        className={Classes.actionBtn}
                        style={{ borderRight: "1px solid rgb(227 227 227 / 50%)" }}
                        onClick={() => navigate(`/mentor/view-profile/${m._id}`)}
                      >
                        <ProfileIcon height="15" fill="#888787" />
                        <span>View Profile</span>
                      </button>

                      <button
                        className={Classes.actionBtn}
                        onClick={() => openBooking(m)}
                        disabled={!stripeConnected}
                        style={{
                          opacity: stripeConnected ? 1 : 0.55,
                          cursor: stripeConnected ? "pointer" : "not-allowed",
                        }}
                        title={
                          stripeConnected
                            ? "Book Meeting"
                            : "Booking disabled: Mentor hasn’t connected Stripe"
                        }
                      >
                        <CalenderSimpleIcon height="15" fill="#888787" />
                        <span>Book Meeting</span>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className={Classes.noMentorMsg}>
              <h5 className={Classes.text}>No mentors match the current filters.</h5>
            </div>
          )}
        </div>
      </>
    </ThemeProvider>
  );
}

/* —— nicer modal styling —— */
const sxModal = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 460,
  maxWidth: "calc(100vw - 32px)",
  bgcolor: "background.paper",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  borderRadius: 3,
  overflow: "hidden",
};

const sxModalHeader = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  p: 2,
};

const sxModalActions = {
  p: 2,
  pt: 1.5,
};

const labelText = { fontSize: 12, color: "text.secondary", mb: 0.5 };
